import { useState, useEffect, useCallback } from "react";
import { useLoader } from "@/hooks/LoaderProvider";
import { useNavigate } from "react-router-dom";
import { PlusOutlined } from "@/config/antd";
import Breadcrumb from "@/components/specific/Breadcrumb";
import PageHeader from "@/components/layout/Header/Page_Header";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import RolesManagementCard from "@/pages/roles_management/components/Roles_Management_Card";
import RoleManagementTable from "@/pages/roles_management/components/RoleManagementTable";
import TrashIcon from "@/assets/icons/web_Icons/trash_red.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllRoles,
  selectRoles,
  selectFetchedRoles,
  selectRoleLoading,
  selectRoleTotal,
  deleteSingleRole,
  resetRoleFormData,
} from "@/store/redux/slices/roles/role_slice";
import EmptyState from "@/pages/roles_management/components/Empty_State";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";

const RolesManagementHome = () => {
  const dispatch = useDispatch();
  const roles = useSelector(selectRoles);
  const fetchedRoles = useSelector(selectFetchedRoles);
  const isLoading = useSelector(selectRoleLoading);
  const totalRoles = useSelector(selectRoleTotal);

  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("Today");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [selectedRoles, setSelectedRoles] = useState([]);
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all");
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const getRolesToUse = useCallback(() => {
    return fetchedRoles && fetchedRoles.length > 0 ? fetchedRoles : roles;
  }, [fetchedRoles, roles]);

  const fetchRoles = useCallback(async () => {
    showLoader();
    try {
      // Fallback to fetch all roles
      await dispatch(
        fetchAllRoles({
          skip: (currentPage - 1) * pageSize,
          limit: pageSize,
        })
      ).unwrap();
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    } finally {
      hideLoader();
    }
  }, [currentPage, dispatch, hideLoader, showLoader]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleRefresh = useCallback(async () => {
    // 1. Reset pagination to first page
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all'
    setActiveFilter("all");

    // 4. Reset the display text for the filter button
    setFilterValue("Today");

    // 5. Fetch fresh data
    showLoader();
    try {
      await dispatch(fetchAllRoles()).unwrap();
    } catch (error) {
      console.error("Error refreshing roles:", error);
    } finally {
      hideLoader();
    }
  }, [dispatch, showLoader, hideLoader]);

  const handleFilterChange = (value) => {
    setFilterValue(value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSetupClick = () => {
    dispatch(resetRoleFormData());
    showLoader();
    setTimeout(() => {
      hideLoader();
      navigate("/dashboard/roles/add-roles-step-1");
    }, 2000);
  };

  const filterOptions = {
    items: [
      { key: "today", label: "Today" },
      { key: "thisWeek", label: "This Week" },
      { key: "thisMonth", label: "This Month" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  const moreOptionsMenu = {
    items: [
      { key: "1", label: "Export" },
      { key: "2", label: "Print" },
    ],
  };

  // Define sort options based on your filter buttons
  const sortOptions = [
    { key: "all", label: "All Roles" },
    { key: "executives", label: "Executives" },
    { key: "leaders", label: "Leaders" },
    { key: "superadmin", label: "Super Admin" },
  ];

  // Transform and filter data for cards (mobile view)
  const getFilteredCardsData = useCallback(() => {
    let filtered = getRolesToUse() || [];

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter(
        (role) =>
          role.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          role.description?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "executives":
        filtered = filtered.filter((role) => role.type === "Executive");
        break;
      case "leaders":
        filtered = filtered.filter((role) => role.type === "Leader");
        break;
      case "superadmin":
        filtered = filtered.filter((role) => role.type === "Super Admin");
        break;
      default:
        break;
    }

    return filtered.map((role) => ({
      id: role._id,
      name: role.name,
      description: role.description,
      type: role.type || "Default",
      status: role.status || "Active",
    }));
  }, [getRolesToUse, searchValue, activeFilter]);

  // Transform and filter data for table (desktop view)
  const getFilteredTableData = useCallback(() => {
    let filtered = getRolesToUse() || [];

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter(
        (role) =>
          role.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          role.description?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "executives":
        filtered = filtered.filter((role) => role.type === "Executive");
        break;
      case "leaders":
        filtered = filtered.filter((role) => role.type === "Leader");
        break;
      case "superadmin":
        filtered = filtered.filter((role) => role.type === "Super Admin");
        break;
      default:
        break;
    }

    return filtered.map((role) => ({
      key: role._id,
      id: role._id,
      name: role.name,
      description: role.description,
      type: role.type || "Default",
      status: role.status || "Active",
      permissions: role.permissions || [],
    }));
  }, [getRolesToUse, searchValue, activeFilter]);

  // Get filtered data
  const filteredTableData = getFilteredTableData();
  const filteredCardsData = getFilteredCardsData();

  const showEmptyState = () => {
    // Show empty state if:
    // 1. No roles at all
    // 2. Search returns no results
    // 3. Filter returns no results
    if (
      !isLoading &&
      (!roles || roles.length === 0 || filteredTableData.length === 0)
    ) {
      let message = "No Roles Found";

      if (searchValue.trim()) {
        message = "No roles found matching your search";
      } else if (activeFilter !== "all") {
        message = `No ${activeFilter
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()} roles found`;
      }

      return <EmptyState onAddNew={handleSetupClick} message={message} />;
    }
    return null;
  };

  // Handle single role deletion
  const handleDelete = async (roleId, roleName) => {
    showLoader();
    try {
      const payload = { id: roleId };
      await dispatch(deleteSingleRole(payload)).unwrap();
      setSuccessMessage(`Role "${roleName}" has been successfully deleted.`);
      setIsSuccessVisible(true);
      fetchRoles();
    } catch (error) {
      setErrorMessage(
        error.message || "Failed to delete role. Please try again."
      );
      setIsErrorVisible(true);
    } finally {
      hideLoader();
    }
  };

  // Handle bulk deletion
  const handleBulkDelete = async () => {
    if (selectedRoles.length === 0) return;

    showLoader();
    try {
      // Send all IDs in a single request
      await dispatch(deleteSingleRole({ id: selectedRoles })).unwrap();

      setSuccessMessage(
        `${selectedRoles.length} roles have been successfully deleted.`
      );
      setIsSuccessVisible(true);
      setSelectedRoles([]); // Clear selection
      fetchRoles();
    } catch (error) {
      setErrorMessage(
        error.message || "Failed to delete roles. Please try again."
      );
      setIsErrorVisible(true);
    } finally {
      hideLoader();
    }
  };

  const handleModify = (role) => {
    navigate(`/dashboard/roles/edit/${role.id || role._id || role.key}/step-1`);
  };

  // breadcrumb items
  const items = [{ title: "Roles Management", link: "/dashboard/roles" }];

  return (
    <div className="w-full">
      <Breadcrumb items={items} />
      <PageHeader title="Roles Management" />

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-col text-left">
            <h1 className="heading-medium text-lighterGrey">
              {totalRoles} Total Roles
            </h1>
            <h1 className="text-regular text-lighterGrey">
              Total number of Roles
            </h1>
          </div>
          <div className="items-center hidden gap-2 lg:flex">
            <Button
              text={`All Roles (${roles?.length || 0})`}
              color="primary"
              className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
              iconPosition="left"
              customBgColor={activeFilter === "all" ? "primary" : "white"}
              customTextColor={activeFilter === "all" ? "white" : "primary"}
              onClick={() => setActiveFilter("all")}
            />
            <Button
              text={`Executives (${
                roles?.filter((role) => role.type === "Executive")?.length || 0
              })`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={
                activeFilter === "executives" ? "primary" : "white"
              }
              customTextColor={
                activeFilter === "executives" ? "white" : "primary"
              }
              onClick={() => setActiveFilter("executives")}
            />
            <Button
              text={`Leaders (${
                roles?.filter((role) => role.type === "Leader")?.length || 0
              })`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "leaders" ? "primary" : "white"}
              customTextColor={activeFilter === "leaders" ? "white" : "primary"}
              onClick={() => setActiveFilter("leaders")}
            />
            <Button
              text={`Super Admin (${
                roles?.filter((role) => role.type === "Super Admin")?.length ||
                0
              })`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={
                activeFilter === "superadmin" ? "primary" : "white"
              }
              customTextColor={
                activeFilter === "superadmin" ? "white" : "primary"
              }
              onClick={() => setActiveFilter("superadmin")}
            />
          </div>
        </div>
        <div className="items-center hidden gap-3 lg:flex">
          <Button
            text={
              selectedRoles.length != 0 ? "DELETE SELECTED" : "ADD NEW ROLES"
            }
            icon={
              selectedRoles.length != 0 ? (
                <img src={TrashIcon} />
              ) : (
                <PlusOutlined />
              )
            }
            color={selectedRoles.length != 0 ? "outline" : "primary"}
            className="border border-primary font-normal text-[11px] !px-3 !py-2 h-8"
            iconPosition="left"
            customBgColor={selectedRoles.length != 0 ? "" : "white"}
            customTextColor={selectedRoles.length != 0 ? "red" : "primary"}
            onClick={
              selectedRoles.length != 0 ? handleBulkDelete : handleSetupClick
            }
          />
        </div>
        <Button
          text="ADD NEW"
          icon={<PlusOutlined />}
          color="primary"
          className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] sm:min-w-[100px] lg:hidden"
          iconPosition="left"
          customBgColor="primary"
          customTextColor="white"
          onClick={handleSetupClick}
        />
      </div>

      <SearchFilterBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onFilterChange={(value) => {
          setActiveFilter(value);
        }}
        filterValue={filterValue}
        filterOptions={filterOptions}
        moreOptionsMenu={moreOptionsMenu}
        placeholder="Search..."
        filterType="dropdown"
        sortOptions={sortOptions}
        searchValue={searchValue}
      />

      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <>
          {showEmptyState() || (
            <>
              <div className="hidden lg:block lg:border-2 border-[#eee] rounded-xl">
                {filteredTableData.length > 0 && (
                  <div className="flex-col hidden p-5 text-left lg:flex">
                    <h1 className="mb-2 font-bold text-regular text-lighterGrey">
                      All Roles for all Account Types
                    </h1>
                    <p className="text-small text-lighterGrey">
                      View all roles for both Corporate users and Merchant
                      Accounts and manage them on this page all in one place
                    </p>
                  </div>
                )}
                <RoleManagementTable
                  data={filteredTableData}
                  setSelectedRoles={setSelectedRoles}
                  onDelete={handleDelete}
                  onModify={handleModify}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:hidden">
                {filteredCardsData.map((role) => (
                  <RolesManagementCard
                    key={role.id}
                    {...role}
                    className="gap-4"
                    onDelete={handleDelete}
                    onModify={handleModify}
                  />
                ))}
              </div>

              {filteredTableData.length > 0 && (
                <div className="mt-6">
                  <CustomPagination
                    current={currentPage}
                    total={totalRoles}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    className="pb-8"
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Success Modal */}
      <Success
        isVisible={isSuccessVisible}
        onClose={() => setIsSuccessVisible(false)}
        header="Success"
        paragraph={successMessage}
        buttonText="Close"
      />

      {/* Error Modal */}
      <Error
        isVisible={isErrorVisible}
        onClose={() => setIsErrorVisible(false)}
        header="Error"
        paragraph={errorMessage}
        buttonText="Try Again"
      />
    </div>
  );
};

export default RolesManagementHome;
